import type { Ticket } from '@centraoneplus/shared-types';

/**
 * Client-side filter + sort for the Backtrack "Filter" tab. Kept as a pure,
 * dependency-free function so it can be unit-tested in isolation (see
 * backtrackFilter.logic.test.ts) and reused by BacktrackPage without dragging
 * React state into the logic.
 *
 * The date range filters on WHEN a ticket was backtracked (`backtrackedAt`),
 * not when it was created, and is inclusive at both ends: `fromDate` starts at
 * 00:00:00.000 and `toDate` ends at 23:59:59.999 of the given local day. Dates
 * are `YYYY-MM-DD` strings from `<input type="date">`; empty string means "no
 * bound".
 */
export interface BacktrackFilterOptions {
  priorities: string[];
  /**
   * Selected pipeline-stage IDs. A ticket matches if its `stage.id` (falling
   * back to `stageId`) is in the list. Optional so existing callers that don't
   * filter by stage keep working unchanged; `undefined`/`[]` means "any stage".
   */
  stages?: string[];
  /** Selected pipeline ID; `''`/`undefined` = any pipeline. */
  pipelineId?: string;
  /**
   * Selected assignee ID; `''`/`undefined` = anyone. The sentinel
   * `UNASSIGNED_ASSIGNEE` matches only tickets with no assignee.
   */
  assigneeId?: string;
  fromDate: string;
  toDate: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

/** Sentinel value for the "Unassigned" option in the assignee filter. */
export const UNASSIGNED_ASSIGNEE = '__unassigned__';

/** `{ id, name }` option pair for the pipeline / assignee dropdowns. */
export interface IdNameOption {
  id: string;
  name: string;
}

/**
 * Distinct pipelines present in the given tickets, sorted by name. Pure +
 * testable; the page memoizes it over the fetched archive page.
 */
export function derivePipelineOptions(rows: Ticket[]): IdNameOption[] {
  const byId = new Map<string, string>();
  for (const t of rows) {
    const pipeline = (t as { pipeline?: { id?: string; name?: string } }).pipeline;
    if (pipeline?.id && !byId.has(pipeline.id)) {
      byId.set(pipeline.id, pipeline.name?.trim() || 'Pipeline');
    }
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Distinct assignees present in the given tickets (by `assigneeId`, labelled
 * from the BFF-enriched `assignee.name`), plus whether any ticket is
 * unassigned so the caller can offer an "Unassigned" option.
 */
export function deriveAssigneeOptions(rows: Ticket[]): {
  options: IdNameOption[];
  hasUnassigned: boolean;
} {
  const byId = new Map<string, string>();
  let hasUnassigned = false;
  for (const t of rows) {
    const assigneeId = (t as { assigneeId?: string }).assigneeId;
    if (!assigneeId) {
      hasUnassigned = true;
      continue;
    }
    if (!byId.has(assigneeId)) {
      const name = (t as { assignee?: { name?: string } }).assignee?.name;
      byId.set(assigneeId, name?.trim() || 'Assigned');
    }
  }
  const options = [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { options, hasUnassigned };
}

const PRIORITY_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

/**
 * A group of pipeline stages that share the same display name. Different
 * pipelines can each have their own "Resolved" stage (distinct IDs), which
 * would otherwise render as duplicate filter chips. Grouping by name collapses
 * them to a single chip whose `ids` covers every matching stage, so filtering
 * by "Resolved" matches tickets in any pipeline's resolved stage.
 */
export interface StageGroup {
  /** Normalized (lower-cased, trimmed) name — stable key. */
  key: string;
  /** Display name (first-seen original casing). */
  name: string;
  /** Colour of the lowest-sortOrder stage in the group. */
  color?: string;
  /** All stage IDs sharing this name. */
  ids: string[];
}

/**
 * Derive the distinct, name-deduped stage groups present in the given tickets,
 * ordered by the group's lowest stage `sortOrder` then name. Pure so it can be
 * unit-tested and memoized in the page.
 */
export function deriveStageGroups(rows: Ticket[]): StageGroup[] {
  const byName = new Map<string, StageGroup & { sortOrder: number }>();
  for (const t of rows) {
    const stage = (t as {
      stage?: { id?: string; name?: string; color?: string; sortOrder?: number };
    }).stage;
    if (!stage?.id || !stage.name) continue;
    const name = stage.name.trim();
    const key = name.toLowerCase();
    const sortOrder = stage.sortOrder ?? 0;
    const existing = byName.get(key);
    if (existing) {
      if (!existing.ids.includes(stage.id)) existing.ids.push(stage.id);
      if (sortOrder < existing.sortOrder) {
        existing.sortOrder = sortOrder;
        existing.color = stage.color;
      }
    } else {
      byName.set(key, { key, name, color: stage.color, ids: [stage.id], sortOrder });
    }
  }
  return [...byName.values()]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    .map(({ sortOrder: _sortOrder, ...group }) => group);
}

export function filterAndSortBacktracked(
  rows: Ticket[],
  { priorities, stages = [], pipelineId, assigneeId, fromDate, toDate, sortBy, sortOrder }: BacktrackFilterOptions,
): Ticket[] {
  const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
  const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;

  const result = rows.filter((t) => {
    if (priorities.length && !priorities.includes(t.priority)) {
      return false;
    }
    if (stages.length) {
      const stageId =
        (t as { stage?: { id?: string } }).stage?.id ??
        (t as { stageId?: string }).stageId;
      if (!stageId || !stages.includes(stageId)) return false;
    }
    if (pipelineId) {
      const pid =
        (t as { pipelineId?: string }).pipelineId ??
        (t as { pipeline?: { id?: string } }).pipeline?.id;
      if (pid !== pipelineId) return false;
    }
    if (assigneeId) {
      const aid = (t as { assigneeId?: string }).assigneeId;
      if (assigneeId === UNASSIGNED_ASSIGNEE) {
        if (aid) return false;
      } else if (aid !== assigneeId) {
        return false;
      }
    }
    if (fromTs !== null || toTs !== null) {
      const backtrackedAt = (t as { backtrackedAt?: string | Date | null }).backtrackedAt;
      if (!backtrackedAt) return false;
      const ts = new Date(backtrackedAt).getTime();
      if (fromTs !== null && ts < fromTs) return false;
      if (toTs !== null && ts > toTs) return false;
    }
    return true;
  });

  const dir = sortOrder === 'asc' ? 1 : -1;
  result.sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortBy === 'priority') {
      av = PRIORITY_RANK[a.priority] ?? -1;
      bv = PRIORITY_RANK[b.priority] ?? -1;
    } else if (sortBy === 'backtrackedAt') {
      av = new Date((a as { backtrackedAt?: string | Date | null }).backtrackedAt || 0).getTime();
      bv = new Date((b as { backtrackedAt?: string | Date | null }).backtrackedAt || 0).getTime();
    } else if (sortBy === 'createdAt') {
      av = new Date(a.createdAt || 0).getTime();
      bv = new Date(b.createdAt || 0).getTime();
    } else {
      av = (a.subject || '').toLowerCase();
      bv = (b.subject || '').toLowerCase();
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  return result;
}
